import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Download, Search, MoreHorizontal, Pencil, Trash2, Sparkles, AlertTriangle, X, Tag as TagIcon, ArrowUp, ArrowDown, FileSpreadsheet, RotateCcw, CalendarX, FileWarning, Copy, Repeat, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { formatDateShort } from "@/lib/format";
import { useI18n, categoryLabel } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import UploadInvoiceModal from "@/components/UploadInvoiceModal";
import BulkUploadModal from "@/components/BulkUploadModal";
import IvaFields from "@/components/IvaFields";
import { useShiftSelection } from "@/lib/useShiftSelection";
import { EditInvoiceModal } from "@/components/invoices/EditInvoiceModal";
import RepeatInvoiceDialog from "@/components/invoices/RepeatInvoiceDialog";

const CURRENCIES = ["EUR", "USD", "GBP", "MXN", "ARS", "COP", "CLP", "BRL", "CHF", "JPY"];

function formatMoney(value, currency = "EUR") {
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency, maximumFractionDigits: 2, useGrouping: "always" }).format(value || 0);
  } catch {
    return `${(value || 0).toFixed(2)} ${currency}`;
  }
}

export default function Invoices({ mode = "all" } = {}) {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filters. `start`/`end` default to the current calendar year so the page
  // loads scoped to "this year" (matches Dashboard + Proveedores defaults).
  // The user can switch to another year via the Year selector or clear both
  // dates for the historic view.
  const _initialYear = new Date().getFullYear();
  // Iter 65 — Bug fix (muntal@gmail.com FRIOLISA case): the default was
  // year-only (`YYYY-01-01` → `YYYY-12-31`) which silently hid every historic
  // invoice from previous years. Users kept uploading old-dated invoices and
  // getting "duplicated" messages while the existing rows appeared to be
  // missing. Widen the default to the last ~2 years so historical FRIOLISA-
  // like invoices stay visible; users can still narrow down manually.
  const _startYear = _initialYear - 1;
  const [filters, setFilters] = useState({
    category: "all",
    type: "all",
    q: "",
    start: `${_startYear}-01-01`,
    end: `${_initialYear}-12-31`,
    date_field: "date",
    needs_review: false,
    min_amount: "",
    max_amount: "",
  });
  const [sort, setSort] = useState({ by: "date", order: "desc" });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPrefill, setModalPrefill] = useState(null); // { vendor, vendor_tax_id, date } when arrived via deeplink
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [repeating, setRepeating] = useState(null); // invoice being repeated (iter 82.4)
  const { selectedIds, isSelected, toggle: toggleSelectAt, toggleAll, allSelected, clear: clearSelection } = useShiftSelection();

  // "Borrar facturas del mes" dialog + suspicious-consolidated banner
  const [deleteMonthOpen, setDeleteMonthOpen] = useState(false);
  const [deleteMonthValue, setDeleteMonthValue] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [deleteMonthBusy, setDeleteMonthBusy] = useState(false);
  const [suspicious, setSuspicious] = useState([]);

  const loadSuspicious = useCallback(async () => {
    try {
      const { data } = await api.get("/invoices/suspicious-consolidated");
      setSuspicious(data.invoices || []);
    } catch (err) {
      // Silently fail — this is just an alert helper
      console.warn("suspicious-consolidated fetch failed:", err);
    }
  }, []);

  useEffect(() => { loadSuspicious(); }, [loadSuspicious]);

  // Dismiss a specific AI flag on an invoice (multi-invoice or suspicious).
  // Both banners re-render off the same state so this refreshes them.
  const dismissInvoiceFlag = async (invoiceId, flag) => {
    try {
      await api.post(`/invoices/${invoiceId}/dismiss-flags`, {
        [flag === "multi_invoice" ? "multi_invoice" : "suspicious"]: true,
      });
      toast.success(lang === "es" ? "Aviso marcado como correcto" : "Flag dismissed");
      // Refresh: suspicious banner + invoice list (so the multi-factura badge
      // disappears in-place without a full reload).
      await loadSuspicious();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteMonth = async () => {
    if (!/^\d{4}-\d{2}$/.test(deleteMonthValue)) {
      toast.error(lang === "es" ? "Formato YYYY-MM" : "Format YYYY-MM");
      return;
    }
    const [y, m] = deleteMonthValue.split("-").map((s) => parseInt(s, 10));
    if (!window.confirm(
      lang === "es"
        ? `¿Borrar TODAS las facturas de ${deleteMonthValue}? Esta acción es irreversible.`
        : `Delete ALL invoices for ${deleteMonthValue}? This cannot be undone.`,
    )) return;
    setDeleteMonthBusy(true);
    try {
      const params = { year: y, month: m };
      // Scope by mode: gastos = expense, ingresos = income, salarios = expense (salary cats handled by reload)
      if (cfg.lockType) params.type = cfg.lockType;
      const { data } = await api.delete("/invoices/by-month", { params });
      toast.success(
        lang === "es"
          ? `Borradas ${data.deleted} facturas de ${data.month}`
          : `Deleted ${data.deleted} invoices for ${data.month}`,
      );
      setDeleteMonthOpen(false);
      load();
      loadSuspicious();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleteMonthBusy(false);
    }
  };

  // Deeplink: when /invoices is opened with ?new_invoice=1&vendor=...&date=...
  // (used by the per-month "+ Mes" buttons in /proveedores), pre-fill the
  // upload modal and clean the URL so refreshes don't re-open it.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("new_invoice") === "1") {
      const prefill = {
        vendor: url.searchParams.get("vendor") || "",
        vendor_tax_id: url.searchParams.get("vendor_tax_id") || "",
        date: url.searchParams.get("date") || "",
      };
      setModalPrefill(prefill);
      setModalOpen(true);
      url.search = "";
      window.history.replaceState(null, "", url.toString());
    }
  }, []);

  // Deeplink: when /invoices is opened with ?focus=<invoice_id> (used by
  // /proveedores → month drill-down), fetch that invoice and open its edit
  // modal directly. Clean the URL so refresh doesn't reopen it.
  const [focusInvoiceId, setFocusInvoiceId] = useState(() => {
    return new URL(window.location.href).searchParams.get("focus") || null;
  });
  useEffect(() => {
    if (!focusInvoiceId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${focusInvoiceId}`);
        if (!cancelled) {
          setEditing(data);
          const url = new URL(window.location.href);
          url.searchParams.delete("focus");
          window.history.replaceState(null, "", url.toString());
          setFocusInvoiceId(null);
        }
      } catch (_err) {
        // Invalid id or wrong owner. Silently drop the param.
        if (!cancelled) {
          const url = new URL(window.location.href);
          url.searchParams.delete("focus");
          window.history.replaceState(null, "", url.toString());
          setFocusInvoiceId(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [focusInvoiceId]);

  // Mode-specific config — locked filters / titles / default new-invoice values.
  // For income / salaries the "vendor" semantic doesn't really apply: it's the
  // *concept* / source of the row. Toggle column labels & search placeholder.
  const isExpenseMode = mode === "all";
  const cfg = {
    all:     { title: t("expensesPageTitle"), crumb: t("invoices"), addLabel: t("addInvoice"), lockType: "expense", lockCategoryKind: null, defaultType: "expense", defaultCategoryKind: null },
    income:  { title: t("incomePageTitle"), crumb: t("incomePage"), addLabel: t("addIncome"), lockType: "income", lockCategoryKind: null, defaultType: "income", defaultCategoryKind: null },
    salaries:{ title: t("salariesPageTitle"), crumb: t("salariesPage"), addLabel: t("addSalary"), lockType: "expense", lockCategoryKind: "salary", defaultType: "expense", defaultCategoryKind: "salary" },
  }[mode] || {};
  const vendorColumnLabel = isExpenseMode ? t("vendor") : t("concept");
  const searchPh = isExpenseMode ? t("searchPlaceholder") : t("searchPlaceholderConcept");
  const bulkButtonLabel = mode === "income"
    ? t("bulkUploadIncome")
    : mode === "salaries"
      ? t("bulkUploadSalaries")
      : t("bulkUpload");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category !== "all") params.category = filters.category;
      if (filters.type !== "all") params.type = filters.type;
      if (cfg.lockType) params.type = cfg.lockType;
      if (cfg.lockCategoryKind) params.category_kind = cfg.lockCategoryKind;
      if (filters.q) params.q = filters.q;
      // Iter 65 — Bug fix (muntal@gmail.com): free-text search should span
      // every year in the DB. Otherwise the default 2-year date range still
      // hides historical FRIOLISA invoices that the OCR flagged as "duplicate".
      // If the user set explicit dates they can narrow down manually.
      const isSearching = !!(filters.q && filters.q.trim());
      if (!isSearching) {
        if (filters.start) params.start = filters.start;
        if (filters.end) params.end = filters.end;
      }
      if (filters.date_field) params.date_field = filters.date_field;
      if (filters.needs_review) params.needs_review = true;
      if (filters.min_amount !== "") params.min_amount = filters.min_amount;
      if (filters.max_amount !== "") params.max_amount = filters.max_amount;
      params.sort_by = sort.by;
      params.sort_order = sort.order;
      const [inv, cats] = await Promise.all([
        api.get("/invoices", { params }),
        api.get("/categories"),
      ]);
      setItems(inv.data);
      setCategories(cats.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filters, sort, cfg.lockType, cfg.lockCategoryKind]);

  useEffect(() => {
    load();
  }, [load]);

  // Reload solo las categorías — útil tras crear una categoría inline desde
  // el modal de subida de factura.
  const reloadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } catch {
      /* silent */
    }
  }, []);

  const catMap = Object.fromEntries(categories.map((c) => [c.name, c]));

  // Selection management (Shift+click range supported via useShiftSelection)
  const allSelectedOnPage = allSelected(items);
  const toggleAllOnPage = () => toggleAll(items);

  const bulkUpdateCategory = async (category) => {
    if (selectedIds.size === 0) return;
    try {
      const { data } = await api.post("/invoices/bulk-update", {
        ids: Array.from(selectedIds),
        category,
      });
      toast.success(`${t("bulkUpdated")}: ${data.modified}`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(t("confirmBulkDelete"))) return;
    try {
      const { data } = await api.post("/invoices/bulk-delete", {
        ids: Array.from(selectedIds),
      });
      toast.success(`${t("bulkDeleted")}: ${data.deleted}`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(t("deleted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const retryOcr = async (inv) => {
    try {
      const { data } = await api.post(`/invoices/${inv.id}/retry-ocr`);
      if (data?.ok) {
        toast.success(lang === "es" ? "OCR completada — datos actualizados" : "OCR succeeded — data updated");
      } else {
        toast.error(data?.error || (lang === "es" ? "OCR falló de nuevo" : "OCR failed again"));
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Iter 57 — "Reintentar con IA avanzada" (Gemini 2.5 Pro). Solo cuando el
  // usuario lo pide manualmente porque cuesta más créditos LLM. A diferencia
  // del retry normal, este SÍ sobreescribe los campos que Flash rellenó mal.
  const retryOcrPro = async (inv) => {
    const toastId = toast.loading(
      lang === "es" ? "Reintentando con IA avanzada…" : "Retrying with advanced AI…"
    );
    try {
      const { data } = await api.post(`/invoices/${inv.id}/retry-ocr-pro`);
      toast.dismiss(toastId);
      if (data?.ok) {
        toast.success(
          lang === "es"
            ? "IA avanzada: datos actualizados"
            : "Advanced AI: data updated"
        );
      } else {
        toast.error(data?.error || (lang === "es" ? "IA avanzada también falló" : "Advanced AI also failed"));
      }
      load();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(formatApiError(err));
    }
  };

  const duplicateInvoice = async (inv) => {
    // POST a new invoice with the same payload (minus id + file_id).
    // The new row opens in the edit modal so the user can tweak the
    // copy (date, amount, invoice number, etc.) before saving again.
    try {
      const payload = {
        vendor: inv.vendor || null,
        vendor_tax_id: inv.vendor_tax_id || null,
        date: inv.date || new Date().toISOString().slice(0, 10),
        amount: Number(inv.amount) || 0,
        subtotal: inv.subtotal != null ? Number(inv.subtotal) : null,
        currency: inv.currency || "EUR",
        category: inv.category || "other",
        type: inv.type || "expense",
        notes: inv.notes || null,
        invoice_number: inv.invoice_number ? `${inv.invoice_number}-COPIA` : null,
        tax: inv.tax != null ? Number(inv.tax) : null,
        tax_rate: inv.tax_rate != null ? Number(inv.tax_rate) : null,
        iva_breakdown: Array.isArray(inv.iva_breakdown) && inv.iva_breakdown.length >= 2
          ? inv.iva_breakdown
          : null,
        is_credit_note: !!inv.is_credit_note,
        line_items: Array.isArray(inv.line_items) ? inv.line_items : null,
        // file_id intentionally omitted — duplicates don't share the original PDF
      };
      const { data } = await api.post("/invoices", payload);
      toast.success(lang === "es" ? "Gasto duplicado — edítalo ahora" : "Invoice duplicated — edit it now");
      await load();
      setEditing(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportCsv = async () => {
    await downloadInvoiceExport("export.csv", "invoices.csv");
  };

  const exportAeat = async () => {
    await downloadInvoiceExport("export-aeat.csv", "aeat-sii.csv");
  };

  const exportXlsx = async () => {
    try {
      const params = {};
      if (filters.category !== "all") params.category = filters.category;
      const lockType = cfg.lockType;
      if (lockType) params.type = lockType;
      else if (filters.type !== "all") params.type = filters.type;
      if (cfg.lockCategoryKind) params.category_kind = cfg.lockCategoryKind;
      if (filters.start) params.date_from = filters.start;
      if (filters.end) params.date_to = filters.end;
      if (filters.q) params.q = filters.q;
      const res = await api.get("/invoices/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const suffix = lockType || (cfg.lockCategoryKind === "salary" ? "salaries" : "all");
      a.download = `facturas-${suffix}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadInvoiceExport = async (path, filename) => {
    const params = new URLSearchParams();
    if (filters.category !== "all") params.set("category", filters.category);
    if (cfg.lockType) params.set("type", cfg.lockType);
    else if (filters.type !== "all") params.set("type", filters.type);
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);
    try {
      const { getToken } = await import("@/lib/api");
      const token = getToken();
      const res = await fetch(`${API}/invoices/${path}?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(lang === "es" ? "Error al exportar" : "Export failed");
    }
  };

  const toggleSort = (column) => {
    setSort((prev) => {
      if (prev.by === column) {
        return { by: column, order: prev.order === "asc" ? "desc" : "asc" };
      }
      // Sensible defaults per column
      const defaultOrder = column === "date" || column === "amount" ? "desc" : "asc";
      return { by: column, order: defaultOrder };
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
            {cfg.crumb}
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
            {cfg.title}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="outline" onClick={exportXlsx} data-testid="export-xlsx-btn">
            <FileSpreadsheet size={14} className="mr-1.5" /> Excel
          </Button>
          {(mode === "income" || mode === "salaries") && (
            <Button
              variant="outline"
              onClick={() => window.print()}
              data-testid={mode === "salaries" ? "print-salaries-btn" : "print-income-btn"}
              title={lang === "es" ? "Imprimir listado (respeta filtros)" : "Print list (respects filters)"}
            >
              <Printer size={14} className="mr-1.5" />
              {lang === "es" ? "Imprimir" : "Print"}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-upload-btn"
          >
            <Sparkles size={14} className="mr-1.5" /> {bulkButtonLabel}
          </Button>
          <Button
            variant="outline"
            onClick={() => setDeleteMonthOpen(true)}
            data-testid="delete-month-btn"
            className="text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]"
            title={lang === "es" ? "Borrar todas las facturas de un mes" : "Delete all invoices in a month"}
          >
            <CalendarX size={14} className="mr-1.5" />
            {lang === "es" ? "Borrar mes" : "Delete month"}
          </Button>
          <Button
            onClick={() => setModalOpen(true)}
            data-testid="add-invoice-btn"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            <Plus size={14} className="mr-1.5" /> {cfg.addLabel}
          </Button>
        </div>
      </div>

      {/* Suspicious consolidated invoices banner */}
      {suspicious.length > 0 && (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-lg bg-[var(--status-warning-bg)] border border-[var(--status-warning)]/40"
          data-testid="suspicious-banner"
        >
          <FileWarning size={18} className="text-[var(--status-warning)] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <p className="text-sm font-medium text-[var(--status-warning)]">
              {lang === "es"
                ? `${suspicious.length} factura(s) sospechosa(s) de ser una consolidación`
                : `${suspicious.length} suspicious consolidated invoice(s) detected`}
            </p>
            <p className="text-[11px] text-[var(--text-muted)]">
              {lang === "es"
                ? "Cada fila tiene un importe ≥10× la mediana mensual — probablemente un único PDF contiene varias facturas. Puedes editar la factura para separarla, o marcarla como correcta si es un pago real de gran importe."
                : "Each row's amount is ≥10× the monthly median — likely a single PDF bundling several invoices. Edit to split it, or dismiss if it's a legitimate large payment."}
            </p>
            <ul className="mt-1 space-y-1">
              {suspicious.slice(0, 5).map((s, i) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-3 bg-[var(--surface)]/70 rounded px-2 py-1.5 text-xs"
                  data-testid={`suspicious-row-${i}`}
                >
                  <span className="min-w-0 flex-1 truncate">
                    <strong>{s.vendor || "?"}</strong>
                    <span className="ml-1.5 text-[var(--text-muted)]">
                      {s.date || "?"} · {formatMoney(s.amount, s.currency)}
                    </span>
                    <span className="ml-1.5 text-[var(--status-warning)] font-medium">
                      {s.ratio}× {lang === "es" ? "la mediana" : "the median"}
                    </span>
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setEditing(s)}
                      data-testid={`suspicious-edit-${i}`}
                      className="px-2 py-1 rounded text-[11px] font-medium bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                      title={lang === "es" ? "Abrir para editar / borrar" : "Open to edit / delete"}
                    >
                      {lang === "es" ? "Rectificar" : "Rectify"}
                    </button>
                    <button
                      onClick={() => dismissInvoiceFlag(s.id, "suspicious")}
                      data-testid={`suspicious-dismiss-${i}`}
                      className="px-2 py-1 rounded text-[11px] font-medium border border-[var(--border-color)] hover:bg-[var(--surface-hover)]"
                      title={lang === "es" ? "Marcar como correcta (no volver a avisar)" : "Mark as correct (stop nagging)"}
                    >
                      {lang === "es" ? "Es correcta" : "It's correct"}
                    </button>
                  </div>
                </li>
              ))}
              {suspicious.length > 5 && (
                <li className="text-[10px] text-[var(--text-muted)] italic px-2">
                  +{suspicious.length - 5} {lang === "es" ? "más" : "more"}
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="col-span-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              placeholder={searchPh}
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="filter-search"
              className="pl-9 h-9"
            />
          </div>
        </div>
        <Select
          value={filters.category}
          onValueChange={(v) => setFilters({ ...filters, category: v })}
        >
          <SelectTrigger data-testid="filter-category" className="h-9"><SelectValue placeholder={t("category")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all")}</SelectItem>
            {(cfg.lockCategoryKind
              ? categories.filter((c) => c.kind === cfg.lockCategoryKind)
              : categories
            ).map((c) => (
              <SelectItem key={c.name} value={c.name}>{categoryLabel(c, lang)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {cfg.lockType ? (
          <div className="h-9" />
        ) : (
        <Select
          value={filters.type}
          onValueChange={(v) => setFilters({ ...filters, type: v })}
        >
          <SelectTrigger data-testid="filter-type" className="h-9"><SelectValue placeholder={t("type")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all")}</SelectItem>
            <SelectItem value="expense">{t("expense")}</SelectItem>
            <SelectItem value="income">{t("income")}</SelectItem>
          </SelectContent>
        </Select>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="h-9" data-testid="filter-start" />
          <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="h-9" data-testid="filter-end" />
        </div>
        {(() => {
          // Year filter: writes start/end to a full-year range in a single
          // action. When the user manually tweaks a date the select falls
          // back to "custom" (no option selected) so both controls stay
          // truthful. Empty string ("Total histórico") clears both dates.
          const cy = new Date().getFullYear();
          const yearsList = [cy, cy - 1, cy - 2, cy - 3, cy - 4];
          const isYearRange = (y) =>
            filters.start === `${y}-01-01` && filters.end === `${y}-12-31`;
          const activeYear = yearsList.find(isYearRange);
          const isTotal = !filters.start && !filters.end;
          // Value: "" for Total, "custom" when start/end don't match any preset,
          // "YYYY" when matching a full year.
          const selectValue = activeYear
            ? String(activeYear)
            : isTotal
              ? "total"
              : "custom";
          return (
            <Select
              value={selectValue}
              onValueChange={(v) => {
                if (v === "total") {
                  setFilters({ ...filters, start: "", end: "" });
                } else if (v === "custom") {
                  // no-op: keeps current manual dates
                } else {
                  setFilters({ ...filters, start: `${v}-01-01`, end: `${v}-12-31` });
                }
              }}
            >
              <SelectTrigger className="h-9" data-testid="filter-year">
                <SelectValue placeholder={lang === "es" ? "Año" : "Year"} />
              </SelectTrigger>
              <SelectContent>
                {yearsList.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
                <SelectItem value="total">{lang === "es" ? "Total histórico" : "Historic total"}</SelectItem>
                {selectValue === "custom" && (
                  <SelectItem value="custom" disabled>{lang === "es" ? "Personalizado" : "Custom"}</SelectItem>
                )}
              </SelectContent>
            </Select>
          );
        })()}
        <Select value={filters.date_field} onValueChange={(v) => setFilters({ ...filters, date_field: v })}>
          <SelectTrigger className="h-9" data-testid="filter-date-field">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date">{mode === "salaries" ? t("salaryDate") : t("invoiceDate")}</SelectItem>
            <SelectItem value="created_at">{t("uploadDate")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="col-span-2 md:col-span-3 grid grid-cols-2 gap-2">
          <Input
            type="number"
            step="0.01"
            placeholder={t("minAmount")}
            value={filters.min_amount}
            onChange={(e) => setFilters({ ...filters, min_amount: e.target.value })}
            className="h-9"
            data-testid="filter-min-amount"
          />
          <Input
            type="number"
            step="0.01"
            placeholder={t("maxAmount")}
            value={filters.max_amount}
            onChange={(e) => setFilters({ ...filters, max_amount: e.target.value })}
            className="h-9"
            data-testid="filter-max-amount"
          />
        </div>
        <div className="col-span-2 md:col-span-5 flex items-center">
          <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={filters.needs_review}
              onChange={(e) => setFilters({ ...filters, needs_review: e.target.checked })}
              data-testid="filter-needs-review"
              className="rounded border-[var(--border-color)] text-[var(--primary)] focus:ring-[var(--primary)]"
            />
            <AlertTriangle size={12} className="text-[var(--status-warning)]" />
            {t("onlyReview")}
          </label>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          className="sticky top-20 z-30 bg-[var(--primary)] text-white rounded-lg shadow-lg px-4 py-2.5 flex items-center gap-3 flex-wrap"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-medium">
            {selectedIds.size} {t("selected")}
          </span>
          <div className="h-4 w-px bg-white/30" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 gap-1.5 bg-white text-[var(--text-primary)] hover:bg-white/90 hover:text-[var(--text-primary)] text-xs px-3"
                data-testid="bulk-change-category"
              >
                <TagIcon size={12} />
                {t("bulkChangeCategory")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto">
              {categories.map((c) => (
                <DropdownMenuItem
                  key={c.name}
                  onClick={() => bulkUpdateCategory(c.name)}
                  data-testid={`bulk-cat-item-${c.name}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: c.color || "#8a8a86" }}
                  />
                  <span>{categoryLabel(c, lang)}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            size="sm"
            variant="ghost"
            onClick={bulkDelete}
            className="h-8 text-white hover:bg-white/20 hover:text-white"
            data-testid="bulk-delete-btn"
          >
            <Trash2 size={12} className="mr-1" /> {t("bulkDelete")}
          </Button>
          <div className="ml-auto">
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              className="h-8 text-white hover:bg-white/20 hover:text-white"
              data-testid="bulk-clear-btn"
            >
              <X size={14} className="mr-1" /> {t("deselect")}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-color)] text-xs uppercase tracking-wider text-[var(--text-muted)]">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelectedOnPage}
                    onChange={toggleAllOnPage}
                    data-testid="select-all-checkbox"
                    className="rounded border-[var(--border-color)] text-[var(--primary)] focus:ring-[var(--primary)]"
                    aria-label="select all"
                  />
                </th>
                <SortableTh column="date" sort={sort} onToggle={toggleSort} label={mode === "salaries" ? t("salaryDate") : t("invoiceDate")} align="left" hidden={mode === "income"} />
                <SortableTh column="created_at" sort={sort} onToggle={toggleSort} label={t("uploadDate")} align="left" />
                <SortableTh column="vendor" sort={sort} onToggle={toggleSort} label={vendorColumnLabel} align="left" />
                <SortableTh column="category" sort={sort} onToggle={toggleSort} label={t("category")} align="left" />
                <th className="text-left px-4 py-3 font-medium">{t("type")}</th>
                <SortableTh column="subtotal" sort={sort} onToggle={toggleSort} label={t("baseImponible")} align="right" />
                <SortableTh column="amount" sort={sort} onToggle={toggleSort} label={t("total")} align="right" />
                <th className="text-right px-4 py-3 font-medium w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={mode === "income" ? 7 : 8} className="px-4 py-12 text-center text-[var(--text-muted)]">...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={mode === "income" ? 7 : 8} className="px-4 py-16 text-center text-[var(--text-muted)]">{t("noInvoices")}</td></tr>
              ) : (
                items.map((inv, idx) => (
                  <tr
                    key={inv.id}
                    className={`border-b border-[var(--border-color)] last:border-0 hover:bg-[var(--surface-hover)] ${isSelected(inv.id) ? "bg-[var(--surface-hover)]" : ""}`}
                    data-testid={`row-invoice-${inv.id}`}
                    onClick={(e) => {
                      // Shift+click anywhere on the row extends the selection range
                      if (e.shiftKey && !["INPUT", "BUTTON", "A", "SELECT", "TEXTAREA"].includes(e.target.tagName)) {
                        e.preventDefault();
                        window.getSelection?.()?.removeAllRanges?.();
                        toggleSelectAt(inv.id, idx, items, true);
                      }
                    }}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected(inv.id)}
                        onChange={() => {}}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelectAt(inv.id, idx, items, e.shiftKey);
                        }}
                        data-testid={`select-${inv.id}`}
                        className="rounded border-[var(--border-color)] text-[var(--primary)] focus:ring-[var(--primary)]"
                        aria-label={`select ${inv.vendor}`}
                      />
                    </td>
                    {mode !== "income" && (
                      <td className="px-4 py-3 font-mono-num text-xs" data-testid={`invoice-date-${inv.id}`}>
                        {inv.date_missing ? (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--status-warning-bg)] text-[var(--status-warning)]"
                            title={lang === "es"
                              ? "La IA no pudo detectar una fecha de factura clara — se usó la fecha de subida como valor por defecto. Edita la factura para corregirla."
                              : "AI couldn't detect a clear invoice date — using upload date as fallback. Edit to correct it."}
                          >
                            <AlertTriangle size={10} />
                            {formatDateShort(inv.date)}
                          </span>
                        ) : (
                          <span
                            className="text-[var(--text-secondary)]"
                            title={inv.date_source ? `${lang === "es" ? "Detectado desde" : "Detected from"}: ${inv.date_source}` : ""}
                          >
                            {formatDateShort(inv.date)}
                          </span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 font-mono-num text-xs text-[var(--text-muted)]" data-testid={`upload-date-${inv.id}`}>
                      {formatDateShort(inv.created_at)}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <span data-testid={`vendor-name-${inv.id}`}>{inv.vendor || <em className="text-[var(--text-muted)] font-normal">{lang === "es" ? "Sin proveedor" : "No vendor"}</em>}</span>
                          {inv.needs_review && (
                            <span
                              title={t("needsReview")}
                              data-testid={`needs-review-${inv.id}`}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[var(--status-warning-bg)] text-[var(--status-warning)]"
                            >
                              <AlertTriangle size={10} />
                              {t("needsReview")}
                            </span>
                          )}
                          {inv.ocr_error && (
                            <span
                              title={inv.ocr_error}
                              data-testid={`ocr-error-${inv.id}`}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[var(--status-negative-bg)] text-[var(--status-negative)] cursor-help"
                            >
                              <AlertTriangle size={10} />
                              {lang === "es" ? "Error OCR" : "OCR error"}
                            </span>
                          )}
                          {inv.is_multi_invoice && (
                            <span
                              data-testid={`multi-invoice-${inv.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[var(--status-warning-bg)] text-[var(--status-warning)]"
                              title={lang === "es"
                                ? "El OCR detectó que este PDF contiene varias facturas dentro de un mismo documento. Recomendación: sepáralo en PDFs individuales y vuelve a subirlos."
                                : "OCR detected that this PDF bundles several invoices in a single document. Recommendation: split it into individual PDFs and re-upload."}
                            >
                              <FileWarning size={10} />
                              {lang === "es" ? "Multi-factura" : "Multi-invoice"}
                              <button
                                onClick={(e) => { e.stopPropagation(); dismissInvoiceFlag(inv.id, "multi_invoice"); }}
                                data-testid={`multi-invoice-dismiss-${inv.id}`}
                                className="ml-0.5 hover:bg-[var(--status-warning)]/20 rounded px-1 -mx-0.5 font-medium"
                                title={lang === "es" ? "Rectificar: marcar como factura correcta" : "Rectify: mark as a single valid invoice"}
                              >
                                ×
                              </button>
                            </span>
                          )}
                        </div>
                        {(inv.vendor_tax_id || inv.invoice_number) && (
                          <div className="flex items-center gap-2 text-[10px] text-[var(--text-muted)] font-mono-num">
                            {inv.vendor_tax_id && (
                              <span data-testid={`vendor-tax-id-${inv.id}`} title="CIF / NIF / VAT">
                                {inv.vendor_tax_id}
                              </span>
                            )}
                            {inv.vendor_tax_id && inv.invoice_number && <span className="text-[var(--border-color)]">·</span>}
                            {inv.invoice_number && (
                              <span data-testid={`invoice-number-${inv.id}`} title={lang === "es" ? "Nº factura" : "Invoice #"}>
                                #{inv.invoice_number}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs"
                        style={{ background: (catMap[inv.category]?.color || "#8a8a86") + "1a", color: catMap[inv.category]?.color || "#5e5e5a" }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: catMap[inv.category]?.color || "#8a8a86" }} />
                        {categoryLabel(catMap[inv.category] || { name: inv.category }, lang)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs ${inv.type === "income" ? "text-[var(--status-positive)]" : "text-[var(--text-secondary)]"}`}>
                        {inv.type === "income" ? t("income") : t("expense")}
                      </span>
                      {inv.is_credit_note && (
                        <span
                          data-testid={`credit-note-badge-${inv.id}`}
                          className="ml-1.5 inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--status-positive-bg)] text-[var(--status-positive)] font-medium"
                        >
                          {t("creditNoteShort")}
                        </span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono-num font-medium ${
                      (inv.type === "income" || inv.is_credit_note) ? "text-[var(--status-positive)]" : ""
                    }`}>
                      {(inv.type === "income" || inv.is_credit_note) ? "+" : "−"} {formatMoney(
                        catMap[inv.category]?.kind === "salary"
                          ? inv.amount
                          : (inv.subtotal != null ? inv.subtotal : (inv.amount - (inv.tax || 0))),
                        inv.currency
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono-num ${
                      (inv.type === "income" || inv.is_credit_note) ? "text-[var(--status-positive)]" : "text-[var(--text-secondary)]"
                    }`}>
                      {(inv.type === "income" || inv.is_credit_note) ? "+" : "−"} {formatMoney(inv.amount, inv.currency)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`row-actions-${inv.id}`}>
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditing(inv)} data-testid={`edit-${inv.id}`}>
                            <Pencil size={14} className="mr-2" /> {t("edit")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => duplicateInvoice(inv)} data-testid={`duplicate-${inv.id}`}>
                            <Copy size={14} className="mr-2" /> {lang === "es" ? "Duplicar" : "Duplicate"}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setRepeating(inv)} data-testid={`repeat-${inv.id}`}>
                            <Repeat size={14} className="mr-2" /> {lang === "es" ? "Repetir cada mes…" : "Repeat monthly…"}
                          </DropdownMenuItem>
                          {inv.ocr_error && inv.file_id && (
                            <DropdownMenuItem onClick={() => retryOcr(inv)} data-testid={`retry-ocr-${inv.id}`}>
                              <RotateCcw size={14} className="mr-2" /> {lang === "es" ? "Reintentar OCR" : "Retry OCR"}
                            </DropdownMenuItem>
                          )}
                          {inv.file_id && (
                            <DropdownMenuItem onClick={() => retryOcrPro(inv)} data-testid={`retry-ocr-pro-${inv.id}`}>
                              <Sparkles size={14} className="mr-2" />
                              {lang === "es" ? "Reintentar con IA avanzada" : "Retry with advanced AI"}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDelete(inv.id)} className="text-[var(--status-negative)]" data-testid={`delete-${inv.id}`}>
                            <Trash2 size={14} className="mr-2" /> {t("delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {!loading && (mode === "income" || mode === "all") && items.length > 0 && (() => {
              // Aggregate visible totals (subtotal / amount), respecting
              // credit notes (which subtract). Mirror the row sign convention.
              let totalBase = 0;
              let totalAmount = 0;
              for (const inv of items) {
                const sign = inv.is_credit_note ? -1 : 1;
                const base = inv.subtotal != null ? inv.subtotal : (inv.amount || 0) - (inv.tax || 0);
                totalBase += sign * (base || 0);
                totalAmount += sign * (inv.amount || 0);
              }
              const baseCurrency = items[0]?.currency || "EUR";
              const isIncome = mode === "income";
              const totalColor = isIncome ? "text-[var(--status-positive)]" : "text-[var(--status-negative)]";
              const footerTestId = isIncome ? "income-totals-footer" : "expense-totals-footer";
              const baseTestId = isIncome ? "income-total-base" : "expense-total-base";
              const amtTestId = isIncome ? "income-total-amount" : "expense-total-amount";
              return (
                <tfoot data-testid={footerTestId}>
                  <tr className="border-t-2 border-[var(--border-color)] bg-[var(--surface-hover)]/40 font-semibold text-sm">
                    <td colSpan={isIncome ? 5 : 6} className="px-4 py-3 uppercase tracking-wider text-xs text-[var(--text-secondary)]">
                      {lang === "es"
                        ? `Total acumulado · ${items.length} ${items.length === 1 ? "factura" : "facturas"}`
                        : `Running total · ${items.length} ${items.length === 1 ? "invoice" : "invoices"}`}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono-num ${totalColor}`} data-testid={baseTestId}>
                      {isIncome ? "+" : "−"} {formatMoney(totalBase, baseCurrency)}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono-num ${totalColor}`} data-testid={amtTestId}>
                      {isIncome ? "+" : "−"} {formatMoney(totalAmount, baseCurrency)}
                    </td>
                    <td className="px-4 py-3" />
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>
      </div>

      <UploadInvoiceModal
        open={modalOpen}
        onOpenChange={(o) => { setModalOpen(o); if (!o) setModalPrefill(null); }}
        categories={categories}
        onCategoriesChange={reloadCategories}
        defaultType={cfg.defaultType}
        defaultCategoryKind={cfg.defaultCategoryKind}
        prefill={modalPrefill}
        onCreated={load}
      />

      <BulkUploadModal open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} mode={mode} />

      {/* Delete month confirmation dialog */}
      <Dialog open={deleteMonthOpen} onOpenChange={setDeleteMonthOpen}>
        <DialogContent data-testid="delete-month-dialog">
          <DialogHeader>
            <DialogTitle>
              {lang === "es" ? "Borrar facturas del mes" : "Delete invoices for month"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[var(--text-secondary)]">
              {lang === "es"
                ? "Selecciona el mes. Se borrarán TODAS las facturas y sus archivos adjuntos. Acción irreversible."
                : "Pick a month. ALL invoices and attached files will be deleted. This cannot be undone."}
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                {lang === "es" ? "Mes" : "Month"}
              </Label>
              <Input
                type="month"
                value={deleteMonthValue}
                onChange={(e) => setDeleteMonthValue(e.target.value)}
                data-testid="delete-month-input"
                className="font-mono-num"
              />
            </div>
            {cfg.lockType && (
              <p className="text-xs text-[var(--text-muted)]">
                {lang === "es"
                  ? `Solo se borrarán facturas de tipo "${cfg.lockType === "expense" ? "gasto" : "ingreso"}".`
                  : `Only "${cfg.lockType}" invoices will be deleted.`}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMonthOpen(false)}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={handleDeleteMonth}
              disabled={deleteMonthBusy}
              data-testid="delete-month-confirm"
              className="bg-[var(--status-negative)] hover:opacity-90 text-white"
            >
              {deleteMonthBusy ? "..." : (lang === "es" ? "Borrar mes" : "Delete month")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditInvoiceModal
        invoice={editing}
        categories={categories}
        onCategoriesChange={reloadCategories}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
      <RepeatInvoiceDialog
        open={!!repeating}
        onOpenChange={(o) => { if (!o) setRepeating(null); }}
        invoice={repeating}
        lang={lang}
        onDone={load}
      />
    </div>
  );
}

/**
 * Autocomplete for vendor name AND CIF in the Gastos edit modal.
 *
 * Watches both fields — typing in either triggers a debounced fetch to
 * `/vendors/suggestions?q=`. When the user picks a suggestion we fill BOTH
 * `vendor` and `vendor_tax_id` in one shot so they don't have to re-type
 * the CIF for a known supplier. Keyboard-accessible (↑/↓/Enter/Esc).
 *
 * Used within EditInvoiceModal — declared at module scope so React sees a
 * stable identity across renders.
 */

function SortableTh({ column, sort, onToggle, label, align = "left", hidden = false }) {
  if (hidden) return null;
  const active = sort.by === column;
  const Arrow = active && sort.order === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onToggle(column)}
        data-testid={`sort-${column}`}
        className={`inline-flex items-center gap-1 transition-colors ${
          align === "right" ? "ml-auto" : ""
        } ${active ? "text-[var(--text-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}
      >
        <span>{label}</span>
        <Arrow size={11} className={active ? "opacity-100" : "opacity-30"} />
      </button>
    </th>
  );
}
